import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Card,
  CardBody,
  CardHeader,
  Badge,
  Icon,
  useColorModeValue,
  Spinner,
  Alert,
  AlertIcon,
  SimpleGrid,
  Divider,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Skeleton,
  Avatar,
  Flex,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  useToast,
  Select,
  Image,
  Checkbox,
  Stack,
  Tooltip,
  Link
} from '@chakra-ui/react';
import { 
  FaUser,
  FaServer,
  FaSignOutAlt,
  FaArrowLeft,
  FaCloud,
  FaEnvelope,
  FaBuilding,
  FaUserTag,
  FaFlask,
  FaCalendarAlt,
  FaClock,
  FaCrown,
  FaGift,
  FaCheckCircle,
  FaHandshake,
  FaComments,
  FaPaperPlane,
  FaExclamationTriangle,
  FaInfoCircle,
  FaTrash,
  FaKey,
  FaCreditCard,
  FaShoppingCart,
  // FaRobot, // Copilot temporarily hidden
  // FaCoins,
  // FaSync,
  // FaReceipt,
  // FaChartBar
} from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import apiService from '../services/api';
// import copilotApiService from '../services/copilotApi';
import { getProductDisplayName } from '../utils/productNames';
import Footer from './Footer';
import NavDropdowns from './NavDropdowns';


const Settings = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const { isOpen: isContactOpen, onOpen: onContactOpen, onClose: onContactClose } = useDisclosure();
  const { isOpen: isCancelOpen, onOpen: onCancelOpen, onClose: onCancelClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  
  // Manage billing state
  const [isCreatingPortalLink, setIsCreatingPortalLink] = useState(false);

  const [userDetails, setUserDetails] = useState(null);
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [error, setError] = useState(null);
  const [subscriptionError, setSubscriptionError] = useState(null);

  // Purchase summary states
  const [purchaseSummary, setPurchaseSummary] = useState(null);
  const [purchaseSummaryLoading, setPurchaseSummaryLoading] = useState(true);
  const [purchaseSummaryError, setPurchaseSummaryError] = useState(null);
  
  // Contact form state
  const [contactForm, setContactForm] = useState({
    subject: '',
    message: '',
    user_name: '',
    user_email: ''
  });
  const [isSubmittingContact, setIsSubmittingContact] = useState(false);
  const [contactError, setContactError] = useState(null);

  // Coupon state
  const [couponCode, setCouponCode] = useState('');
  const [isValidatingCoupon, setIsValidatingCoupon] = useState(false);
  const [couponError, setCouponError] = useState(null);
  const [couponSuccess, setCouponSuccess] = useState(null);

  // Delete account state
  const [deleteForm, setDeleteForm] = useState({
    email: '',
    reason: '',
    additional_feedback: ''
  });
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  // Reset password state
  const [isResettingPassword, setIsResettingPassword] = useState(false);

  // Notification preferences state
  const [notificationPreferences, setNotificationPreferences] = useState({
    job_notification: true,
    credit_expiry_notification: true
  });
  const [isUpdatingPreferences, setIsUpdatingPreferences] = useState(false);

  // Copilot billing state — temporarily hidden
  /*
  const [copilotBalance, setCopilotBalance] = useState(null);
  const [copilotBalanceLoading, setCopilotBalanceLoading] = useState(true);
  const [copilotUsage, setCopilotUsage] = useState(null);
  const [copilotUsageLoading, setCopilotUsageLoading] = useState(true);
  const [isBuyingCopilotCredits, setIsBuyingCopilotCredits] = useState(false);
  const [isSyncingBalance, setIsSyncingBalance] = useState(false);
  const [isOpeningCopilotPortal, setIsOpeningCopilotPortal] = useState(false);
  const [usageDateFrom, setUsageDateFrom] = useState('');
  const [usageDateTo, setUsageDateTo] = useState('');
  */

  // Color mode values
  const bg = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  useEffect(() => {
    fetchUserDetails();
    fetchSubscriptionDetails();
    fetchPurchaseSummary();
    // fetchCopilotBalance();
    // fetchCopilotUsage();
  }, []);

  const fetchUserDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const details = await apiService.getUserDetails();
      setUserDetails(details);
      
      // Set notification preferences from user details
      // ... existing code ...
      // Set notification preferences from user details
      if (details) {
        setNotificationPreferences({
          job_notification: details.job_notification ?? true,
          credit_expiry_notification: details.credit_expiry_notification ?? true,
          newsletter_subscription: details.newsletter_subscription ?? false,
          allow_training: details.allow_training ?? false
        });
      }
    } catch (err) {
      console.error('Failed to fetch user details:', err);
      setError('Failed to load user details');
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscriptionDetails = async () => {
    try {
      setSubscriptionLoading(true);
      setSubscriptionError(null);
      const details = await apiService.getSubscriptionDetails();
      setSubscriptionDetails(details);
    } catch (err) {
      console.error('Failed to fetch subscription details:', err);
      setSubscriptionError('Failed to load subscription details');
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const fetchPurchaseSummary = async () => {
    try {
      setPurchaseSummaryLoading(true);
      setPurchaseSummaryError(null);
      const summary = await apiService.getPurchaseSummary();
      setPurchaseSummary(summary);
    } catch (err) {
      console.error('Failed to fetch purchase summary:', err);
      setPurchaseSummaryError('Failed to load purchase summary');
    } finally {
      setPurchaseSummaryLoading(false);
    }
  };

  /*
  const fetchCopilotBalance = async () => {
    try {
      setCopilotBalanceLoading(true);
      const data = await copilotApiService.getBalance();
      setCopilotBalance(data);
    } catch (err) {
      setCopilotBalance({ balance_cents: 0, balance_display: '0.00', has_credits: false });
    } finally {
      setCopilotBalanceLoading(false);
    }
  };

  const fetchCopilotUsage = async (fromDate, toDate) => {
    try {
      setCopilotUsageLoading(true);
      const data = await copilotApiService.getUsage(fromDate, toDate);
      setCopilotUsage(data);
    } catch (err) {
      setCopilotUsage(null);
    } finally {
      setCopilotUsageLoading(false);
    }
  };

  const handleBuyCopilotCredits = async () => {
    try {
      setIsBuyingCopilotCredits(true);
      const data = await copilotApiService.buyCredits();
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
    } catch (err) {
      toast({ title: 'Error', description: err.message || 'Failed to start checkout', status: 'error', duration: 5000, isClosable: true });
    } finally {
      setIsBuyingCopilotCredits(false);
    }
  };

  const handleSyncCopilotBalance = async () => {
    try {
      setIsSyncingBalance(true);
      const data = await copilotApiService.syncBalance();
      setCopilotBalance({ balance_cents: data.balance_cents, balance_display: data.balance_display, has_credits: data.balance_cents > 0 });
      toast({ title: 'Balance Synced', description: `Current balance: €${data.balance_display}`, status: 'success', duration: 3000, isClosable: true });
    } catch (err) {
      toast({ title: 'Sync Failed', description: err.message, status: 'error', duration: 5000, isClosable: true });
    } finally {
      setIsSyncingBalance(false);
    }
  };

  const handleCopilotPortal = async () => {
    try {
      setIsOpeningCopilotPortal(true);
      const data = await copilotApiService.getBillingPortal();
      if (data.customer_portal_url) {
        window.open(data.customer_portal_url, '_blank');
      }
    } catch (err) {
      toast({ title: 'Error', description: err.message || 'Failed to open billing portal', status: 'error', duration: 5000, isClosable: true });
    } finally {
      setIsOpeningCopilotPortal(false);
    }
  };
  */

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatSubscriptionDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getSubscriptionStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'green';
      case 'inactive':
      case 'cancelled':
        return 'red';
      case 'trial':
        return 'blue';
      case 'pending':
        return 'yellow';
      default:
        return 'gray';
    }
  };

  const getTierIcon = (tier) => {
    switch (tier?.toLowerCase()) {
      case 'tenhours':
        return FaClock;
      case 'twentyhours':
        return FaClock;
      case 'enterprise':
        return FaHandshake;
      case 'free':
        return FaGift;      
      default:
        return FaUser;
    }
  };

  const handleContactInputChange = (e) => {
    const { name, value } = e.target;
    setContactForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleManageBilling = async () => {
    try {
      setIsCreatingPortalLink(true);
      
      const response = await apiService.createCustomerPortalLink();
      
      if (response.success && response.customer_portal_url) {
        // Redirect to Stripe customer portal
        window.location.href = response.customer_portal_url;
      } else {
        toast({
          title: 'Error',
          description: response.error || 'Failed to access billing portal. Please try again.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (err) {
      console.error('Failed to create portal link:', err);
      toast({
        title: 'Error',
        description: err.message || 'Failed to access billing portal. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsCreatingPortalLink(false);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    
    if (!contactForm.subject.trim() || !contactForm.message.trim()) {
      setContactError('Subject and message are required');
      return;
    }

    setIsSubmittingContact(true);
    setContactError(null);

    try {
      await apiService.sendContactMessage(contactForm);
      
      toast({
        title: 'Message Sent Successfully',
        description: 'Thank you for contacting us. We will get back to you soon!',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      // Reset form and close modal
      setContactForm({
        subject: '',
        message: '',
        user_name: '',
        user_email: ''
      });
      onContactClose();
    } catch (err) {
      console.error('Failed to send message:', err);
      setContactError('Failed to send message. Please try again.');
    } finally {
      setIsSubmittingContact(false);
    }
  };

  const openContactModal = () => {
    // Pre-fill user details if available
    if (userDetails) {
      setContactForm(prev => ({
        ...prev,
        user_name: `${userDetails.first_name} ${userDetails.last_name}`,
        user_email: userDetails.email
      }));
    }
    onContactOpen();
  };

  const openContactSalesModal = () => {
    // Pre-fill user details and set subject for sales inquiry
    if (userDetails) {
      setContactForm(prev => ({
        ...prev,
        user_name: `${userDetails.first_name} ${userDetails.last_name}`,
        user_email: userDetails.email,
        subject: 'Enterprise Plan Inquiry',
        message: 'Hello, I am interested in learning more about the Enterprise plan and would like to discuss pricing and features. Please contact me to schedule a consultation.\n\nThank you!'
      }));
    }
    onContactOpen();
  };

  const handleUpgradeToPremium = async () => {
    try {
      setIsSubmittingContact(true); // Reuse loading state
      
      const checkoutData = {
        subscription_tier: 'premium'
      };
      
      const response = await apiService.createSubscriptionCheckout(checkoutData);
      
      if (response.checkout_url) {
        // Redirect to Stripe checkout
        window.location.href = response.checkout_url;
      } else {
        toast({
          title: 'Error',
          description: 'Failed to create checkout session. Please try again.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Failed to create checkout session:', error);
      toast({
        title: 'Error',
        description: 'Failed to start upgrade process. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmittingContact(false);
    }
  };

  const handleCancelSubscription = async () => {
    try {
      setIsSubmittingContact(true);
      
      // Call API to cancel subscription
      await apiService.cancelSubscription();
      
      toast({
        title: 'Subscription Cancelled',
        description: 'Your subscription has been cancelled. You will retain access until the end of your current billing period.',
        status: 'info',
        duration: 5000,
        isClosable: true,
      });
      
      // Close the confirmation modal
      onCancelClose();
      
      // Refresh subscription details
      await fetchSubscriptionDetails();
    } catch (error) {
      console.error('Failed to cancel subscription:', error);
      toast({
        title: 'Error',
        description: 'Failed to cancel subscription. Please try again or contact support.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmittingContact(false);
    }
  };

  const handleValidateCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponError('Please enter a coupon code');
      return;
    }

    setIsValidatingCoupon(true);
    setCouponError(null);
    setCouponSuccess(null);

    try {
      // First validate the promotion code
      const validationResponse = await apiService.validatePromotionCode(couponCode);
      
      // If validation is successful, proceed to checkout with the promotion code
      const checkoutData = {
        subscription_tier: 'premium',
        promotion_code: couponCode
      };
      
      const checkoutResponse = await apiService.createSubscriptionCheckout(checkoutData);
      
      if (checkoutResponse.checkout_url) {
        // Redirect to Stripe checkout with promotion code applied
        window.location.href = checkoutResponse.checkout_url;
      } else {
        setCouponError('Failed to create checkout session. Please try again.');
      }
    } catch (error) {
      console.error('Failed to validate coupon or create checkout:', error);
      setCouponError('Invalid coupon code or coupon has expired');
    } finally {
      setIsValidatingCoupon(false);
    }
  };

  const handleCouponInputChange = (e) => {
    setCouponCode(e.target.value);
    // Clear errors when user starts typing
    if (couponError) setCouponError(null);
    if (couponSuccess) setCouponSuccess(null);
  };

  const handleDeleteInputChange = (e) => {
    const { name, value } = e.target;
    setDeleteForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDeleteAccount = async (e) => {
    e.preventDefault();
    
    if (!deleteForm.email.trim() || !deleteForm.reason.trim()) {
      setDeleteError('Email and reason are required');
      return;
    }

    if (deleteForm.email !== userDetails?.email) {
      setDeleteError('Email does not match your account email');
      return;
    }

    setIsDeletingAccount(true);
    setDeleteError(null);

    try {
      await apiService.deleteUserAccount(deleteForm);
      
      toast({
        title: 'Account Deleted',
        description: 'Your account has been successfully deleted. You will be logged out shortly.',
        status: 'info',
        duration: 5000,
        isClosable: true,
      });

      // Close modal and logout after a short delay
      onDeleteClose();
      setTimeout(() => {
        logout();
        navigate('/');
      }, 2000);
    } catch (error) {
      console.error('Failed to delete account:', error);
      setDeleteError('Failed to delete account. Please try again or contact support.');
    } finally {
      setIsDeletingAccount(false);
    }
  };

  const openDeleteModal = () => {
    // Pre-fill email if available
    if (userDetails) {
      setDeleteForm(prev => ({
        ...prev,
        email: userDetails.email
      }));
    }
    onDeleteOpen();
  };

  const handleResetPasswordRequest = async () => {
    if (!userDetails?.email) {
      toast({
        title: 'Error',
        description: 'User email not found. Cannot send reset password link.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setIsResettingPassword(true);
    try {
      await apiService.resetPasswordRequest(userDetails.email);
      toast({
        title: 'Password Reset Link Sent',
        description: 'A password reset link has been sent to your email address.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      onContactClose(); // Close contact modal after successful request
    } catch (error) {
      console.error('Failed to send password reset link:', error);
      toast({
        title: 'Error Sending Password Reset Link',
        description: 'Failed to send password reset link. Please try again or contact support.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsResettingPassword(false);
    }
  };

  const handleResetPassword = async () => {
    if (!userDetails?.email) {
      toast({
        title: 'Error',
        description: 'Unable to get your email address. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setIsResettingPassword(true);

    try {
      await apiService.resetPasswordRequest(userDetails.email);
      
      toast({
        title: 'Reset Link Sent',
        description: `A password reset link has been sent to ${userDetails.email}. Please check your email and follow the instructions.`,
        status: 'success',
        duration: 8000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Failed to send reset password email:', error);
      toast({
        title: 'Error',
        description: 'Failed to send password reset email. Please try again later.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsResettingPassword(false);
    }
  };

  const handleUpgrade10Hours = async () => {
    try {
      setIsSubmittingContact(true);
      
      const checkoutData = {
        subscription_tier: 'tenhours'
      };
      
      const response = await apiService.createSubscriptionCheckout(checkoutData);
      
      if (response.checkout_url) {
        window.location.href = response.checkout_url;
      } else {
        toast({
          title: 'Error',
          description: 'Failed to create checkout session. Please try again.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Failed to create checkout session:', error);
      toast({
        title: 'Error',
        description: 'Failed to start upgrade process. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmittingContact(false);
    }
  };

  const handleUpgrade20Hours = async () => {
    try {
      setIsSubmittingContact(true);
      
      const checkoutData = {
        subscription_tier: 'twentyhours'
      };
      
      const response = await apiService.createSubscriptionCheckout(checkoutData);
      
      if (response.checkout_url) {
        window.location.href = response.checkout_url;
      } else {
        toast({
          title: 'Error',
          description: 'Failed to create checkout session. Please try again.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Failed to create checkout session:', error);
      toast({
        title: 'Error',
        description: 'Failed to start upgrade process. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmittingContact(false);
    }
  };

  const handleNotificationPreferenceChange = async (field, value) => {
    // Optimistically update UI
    setNotificationPreferences(prev => ({
      ...prev,
      [field]: value
    }));

    setIsUpdatingPreferences(true);
    try {
      // Call API to update user preferences
      await apiService.updateUserPreferences({
        [field]: value
      });
      
      toast({
        title: 'Preferences Updated',
        description: 'Your notification preferences have been saved.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Failed to update notification preferences:', error);
      
      // Revert on error
      setNotificationPreferences(prev => ({
        ...prev,
        [field]: !value
      }));
      
      toast({
        title: 'Error',
        description: 'Failed to update preferences. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsUpdatingPreferences(false);
    }
  };

  if (loading || subscriptionLoading) {
    return (
      <Box minH="100vh" bg={bg} display="flex" alignItems="center" justifyContent="center">
        <VStack spacing={4}>
          <Spinner size="xl" color="brand.500" thickness="4px" />
          <Text>Loading settings...</Text>
        </VStack>
      </Box>
    );
  }

  return (
    <Box minH="100vh" bg={bg}>
      {/* Header */}
      <Box bg="white" shadow="sm" borderBottom="1px" borderColor="gray.200">
        <Container maxW="7xl" py={4}>
          <HStack justify="space-between">
            <HStack 
              spacing={1} 
              cursor="pointer" 
              onClick={() => navigate('/')} 
              _hover={{ opacity: 0.8 }} 
              transition="opacity 0.2s"
              borderRadius="md"
              p={2}
              _active={{ transform: "scale(0.98)" }}
            >

              <Image
                src="/simple-logo.png"
                alt="SRS Logo"
                boxSize={14}
                objectFit="contain"
              />
              <Box>
                <Heading size="lg" color="brand.500">SRS</Heading>
                <Text fontSize="sm" color="gray.600">Scale-Resolving Simulation at Scale</Text>
              </Box>
            </HStack>
            <HStack spacing={4}>
              <NavDropdowns />
              <Button
                leftIcon={<FaCloud />}
                colorScheme="brand"
                variant="ghost"
                size="sm"
                onClick={() => navigate('/dashboard')}
              >
                Dashboard
              </Button>
              <Button
                leftIcon={<FaSignOutAlt />}
                variant="ghost"
                size="sm"
                onClick={handleLogout}
              >
                Sign Out
              </Button>
            </HStack>
          </HStack>
        </Container>
      </Box>


      <Container maxW="7xl" py={8}>
        <VStack spacing={8} align="stretch">
          {error ? (
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              {error}
              <Button ml={4} size="sm" onClick={fetchUserDetails}>
                Retry
              </Button>
            </Alert>
          ) : userDetails ? (
            <>
              {/* User Profile Section */}
              <Card bg={cardBg} shadow="sm" borderRadius="lg">
                <SimpleGrid columns={{ base: 1, md: 4 }} spacing={0}>
                  {/* Avatar Section */}
                  <Box bg="brand.50" p={6} borderLeftRadius="lg" display="flex" flexDirection="column" alignItems="center" justifyContent="center">
                    <Avatar 
                      size="xl" 
                      name={`${userDetails.first_name} ${userDetails.last_name}`}
                      bg="brand.500"
                      mb={4}
                    />
                    <VStack spacing={2}>
                      <Badge 
                        colorScheme={getSubscriptionStatusColor(subscriptionDetails?.subscription_status)}
                        variant="solid"
                        textTransform="capitalize"
                        borderRadius="full"
                        px={3}
                      >
                        {subscriptionDetails?.subscription_status || 'No Subscription'}
                      </Badge>
                      {subscriptionDetails?.subscription_tier && (
                        <HStack spacing={1}>
                          <Icon as={getTierIcon(subscriptionDetails.subscription_tier)} color="brand.500" boxSize={4} />
                          <Text fontSize="sm" color="brand.600" fontWeight="bold" textTransform="capitalize">
                            {subscriptionDetails.subscription_tier}
                          </Text>
                        </HStack>
                      )}
                    </VStack>
                  </Box>
                  
                  {/* Details Section */}
                  <Box gridColumn={{ base: "1", md: "2 / -1" }} p={6}>
                    <VStack align="start" spacing={4}>
                      <HStack justify="space-between" w="full" align="start">
                        <Box>
                          <Heading size="lg" color="gray.800" mb={1}>
                            {userDetails.first_name} {userDetails.last_name}
                          </Heading>
                          <HStack spacing={2}>
                            <Icon as={FaEnvelope} color="gray.500" boxSize={4} />
                            <Text color="gray.600">{userDetails.email}</Text>
                          </HStack>
                        </Box>
                        
                        <HStack spacing={3}>
                        {/*
                          <Button
                            leftIcon={<FaKey />}
                            colorScheme="gray"
                            variant="outline"
                            size="sm"
                            onClick={handleResetPassword}
                            isLoading={isResettingPassword}
                            loadingText="Sending..."
                          >
                            Reset Password
                          </Button>
                        */}
                          <Button
                            leftIcon={<FaComments />}
                            colorScheme="brand"
                            variant="outline"
                            size="sm"
                            onClick={openContactModal}
                          >
                            Contact Us
                          </Button>
                        </HStack>
                      </HStack>
                      
                      <Divider />
                      
                      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} w="full">
                        <Box p={4} bg="gray.50" borderRadius="md">
                          <HStack spacing={3} mb={2}>
                            <Icon as={FaBuilding} boxSize={5} color="brand.500" />
                            <Text fontSize="sm" fontWeight="semibold" color="gray.700">Organization</Text>
                          </HStack>
                          <Text color="gray.800" fontWeight="medium">
                            {userDetails.organization || 'Not specified'}
                          </Text>
                        </Box>
                        
                        <Box p={4} bg="gray.50" borderRadius="md">
                          <HStack spacing={3} mb={2}>
                            <Icon as={FaUserTag} boxSize={5} color="brand.500" />
                            <Text fontSize="sm" fontWeight="semibold" color="gray.700">Role</Text>
                          </HStack>
                          <Text color="gray.800" fontWeight="medium">
                            {userDetails.role || 'Not specified'}
                          </Text>
                        </Box>
                        
                        <Box p={4} bg="gray.50" borderRadius="md">
                          <HStack spacing={3} mb={2}>
                            <Icon as={FaFlask} boxSize={5} color="brand.500" />
                            <Text fontSize="sm" fontWeight="semibold" color="gray.700">Research Area</Text>
                          </HStack>
                          <Text color="gray.800" fontWeight="medium">
                            {userDetails.research_area || 'Not specified'}
                          </Text>
                        </Box>
                      </SimpleGrid>
                      
                      <Divider />
                      
                      {/* Notification Preferences */}
                      <Box>
                        <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={3}>
                          Notification Preferences
                        </Text>
                        <VStack spacing={3} align="start" pl={2}>
                          {/*
                          <Checkbox
                            isChecked={notificationPreferences.job_notification}
                            onChange={(e) => handleNotificationPreferenceChange('job_notification', e.target.checked)}
                            colorScheme="brand"
                            isDisabled={isUpdatingPreferences}
                          >
                            <Text fontSize="sm" color="gray.700">
                              Receive notification on job completion
                            </Text>
                          </Checkbox>
                          */}
                          
                          <Checkbox
                            isChecked={notificationPreferences.credit_expiry_notification}
                            onChange={(e) => handleNotificationPreferenceChange('credit_expiry_notification', e.target.checked)}
                            colorScheme="brand"
                            isDisabled={isUpdatingPreferences}
                          >
                            <Text fontSize="sm" color="gray.700">
                              Notify me at 90% credit usage
                            </Text>
                          </Checkbox>

                          <Checkbox
                            isChecked={notificationPreferences.newsletter_subscription}
                            onChange={(e) => handleNotificationPreferenceChange('newsletter_subscription', e.target.checked)}
                            colorScheme="brand"
                            isDisabled={isUpdatingPreferences}
                          >
                            <Text fontSize="sm" color="gray.700">
                              Subscribe to newsletter, stay updated on new features and updates
                            </Text>
                          </Checkbox>
                          
                          <Tooltip
                            label="This option is only available for paid users"
                            isDisabled={purchaseSummary?.product_name === 'On Demand Usage'}
                            hasArrow
                            placement="top"
                          >
                            <Box>
                              <Checkbox
                                isChecked={notificationPreferences.allow_training}
                                onChange={(e) => handleNotificationPreferenceChange('allow_training', e.target.checked)}
                                colorScheme="brand"
                                isDisabled={isUpdatingPreferences || purchaseSummary?.product_name !== 'On Demand Usage'}
                              >
                                <Text fontSize="sm" color="gray.700">
                                Help improve simulation accuracy and reduce carbon footprint by allowing access to parts of simulation data. See <Link as={RouterLink} to="/privacy" color="brand.500" fontWeight="medium">privacy policy</Link>.
                                </Text>
                              </Checkbox>
                            </Box>
                          </Tooltip>

                        </VStack>
                      </Box>
                    </VStack>
                  </Box>
                </SimpleGrid>
              </Card>

              {/* Purchase Summary Details */}
              <Card bg={cardBg} shadow="sm">
                <CardHeader>
                  <HStack justify="space-between" align="center">
                    <Heading size="md">Purchase Summary</Heading>
                    {(subscriptionError || purchaseSummaryError) && (
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={() => {
                          if (subscriptionError) fetchSubscriptionDetails();
                          if (purchaseSummaryError) fetchPurchaseSummary();
                        }}
                      >
                        Retry
                      </Button>
                    )}
                    <HStack spacing={3}>
                     {subscriptionDetails && purchaseSummary && (
                    <Button
                      leftIcon={<Icon as={FaCreditCard} />}
                      colorScheme="brand"
                      size="sm"
                      onClick={handleManageBilling}
                      isLoading={isCreatingPortalLink}
                      loadingText="Loading..."
                    >
                      Manage Billing
                    </Button>
                  )}
                  <Button 
                    colorScheme="brand"
                    variant="outline"
                    size="md"        
                    minW="auto"
                    px={3}  
                    onClick={() => navigate('/buy-credits')}
                  >
                    <Icon as={FaShoppingCart} />
                </Button>          
                  </HStack>                
                  </HStack> 
                </CardHeader>
                <CardBody pt={0}>
                  {(subscriptionError || purchaseSummaryError) ? (
                    <Alert status="error" borderRadius="md">
                      <AlertIcon />
                      {subscriptionError || purchaseSummaryError}
                    </Alert>
                  ) : (subscriptionDetails && purchaseSummary) ? (
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
                      {/* Purchase Summary Info */}
                      <Box>
                        <Table variant="simple" size="sm">
                          <Tbody>
                            {purchaseSummary.product_name && (
                              <Tr>
                                <Td fontWeight="medium" color="gray.600" w="140px" py={3} pl={0}>
                                  <HStack spacing={2}>
                                    <Icon as={FaCrown} boxSize={4} />
                                    <Text>Latest Product</Text>
                                  </HStack>
                                </Td>
                                <Td py={3}>
                                  <Badge 
                                    variant="subtle" 
                                    colorScheme={purchaseSummary.product_name === 'On Demand Usage' ? 'blue' : purchaseSummary.product_name === 'Free Tier' ? 'yellow' : 'gray'}
                                    textTransform="capitalize"
                                  >
                                    {getProductDisplayName(purchaseSummary.product_name)}
                                  </Badge>
                                </Td>
                              </Tr>
                            )}
                            {purchaseSummary.status && (
                              <Tr>
                                <Td fontWeight="medium" color="gray.600" py={3} pl={0}>
                                  <HStack spacing={2}>
                                    <Icon as={FaCheckCircle} boxSize={4} />
                                    <Text>Purchase Status</Text>
                                  </HStack>
                                </Td>
                                <Td py={3}>
                                  <Badge 
                                    variant="subtle" 
                                    colorScheme={purchaseSummary.status === 'succeeded' ? 'green' : purchaseSummary.status === 'pending' ? 'yellow' : 'gray'}
                                    textTransform="capitalize"
                                  >
                                    {purchaseSummary.status}
                                  </Badge>
                                </Td>
                              </Tr>
                            )}
                            <Tr>
                              <Td fontWeight="medium" color="gray.600" py={3} pl={0}>
                                <HStack spacing={2}>
                                  <Icon as={FaServer} boxSize={4} />
                                  <Text>Total Purchases</Text>
                                </HStack>
                              </Td>
                              <Td py={3}>
                                {purchaseSummary.total_purchases}
                              </Td>
                            </Tr>
                            <Tr>
                              <Td fontWeight="medium" color="gray.600" py={3} pl={0} borderBottom="none">
                                <HStack spacing={2}>
                                  <Icon as={FaCalendarAlt} boxSize={4} />
                                  <Text>Total Spent</Text>
                                </HStack>
                              </Td>
                              <Td py={3} borderBottom="none">
                                €{(purchaseSummary.total_amount / 100).toFixed(0)}
                              </Td>
                            </Tr>
                          </Tbody>
                        </Table>
                      </Box>

                      {/* Purchase Timeline */}
                      <Box>
                        <Table variant="simple" size="sm">
                          <Tbody>
                            <Tr>
                              <Td fontWeight="medium" color="gray.600" w="140px" py={3} pl={0}>
                                <HStack spacing={2}>
                                  <Icon as={FaCalendarAlt} boxSize={4} />
                                  <Text>Member Since</Text>
                                </HStack>
                              </Td>
                              <Td py={3}>{formatDate(subscriptionDetails.created_at)}</Td>
                            </Tr>
                            <Tr>
                              <Td fontWeight="medium" color="gray.600" py={3} pl={0}>
                                <HStack spacing={2}>
                                  <Icon as={FaClock} boxSize={4} />
                                  <Text>Last Updated</Text>
                                </HStack>
                              </Td>
                              <Td py={3}>{formatDate(subscriptionDetails.updated_at)}</Td>
                            </Tr>
                            <Tr>
                              <Td fontWeight="medium" color="gray.600" py={3} pl={0} borderBottom="none">
                                <HStack spacing={2}>
                                  <Icon as={FaCalendarAlt} boxSize={4} />
                                  <Text>Last Purchase</Text>
                                </HStack>
                              </Td>
                              <Td py={3} borderBottom="none">
                                {purchaseSummary.last_purchase 
                                  ? formatDate(purchaseSummary.last_purchase)
                                  : 'Never'
                                }
                              </Td>
                            </Tr>
                          </Tbody>
                        </Table>
                      </Box>
                    </SimpleGrid>
                  ) : (
                    <Box textAlign="center" py={8}>
                      <Icon as={FaCrown} color="gray.400" boxSize={12} mb={4} />
                      <Heading size="md" color="gray.500" mb={2}>
                        No Purchase Data Found
                      </Heading>
                      <Text color="gray.600">
                        You don't have any purchase history yet.
                      </Text>
                    </Box>
                  )}
                </CardBody>
              </Card>

              {/* Copilot billing section temporarily hidden */}

              {/* Delete Account Section */}
              <Card bg="red.50" shadow="sm" border="1px solid" borderColor="red.200">
                <CardHeader>
                  <HStack spacing={3}>
                    <Icon as={FaTrash} color="red.500" boxSize={6} />
                    <Box>
                      <Heading size="md" color="red.700">Delete Account</Heading>
                      <Text fontSize="sm" color="red.600">
                        Permanently delete your account and all associated data
                      </Text>
                    </Box>
                  </HStack>
                </CardHeader>
                <CardBody pt={0}>
                  <VStack spacing={4} align="stretch">
                    <Alert status="warning" borderRadius="md">
                      <AlertIcon />
                      <Box>
                        <Text fontWeight="semibold">This action cannot be undone!</Text>
                        <Text fontSize="sm">
                          Deleting your account will permanently remove all your data, jobs, and subscription.
                        </Text>
                      </Box>
                    </Alert>

                    <Box bg="white" p={4} borderRadius="md" border="1px solid" borderColor="red.200">
                      <VStack spacing={3} align="start">
                        <Text fontSize="sm" fontWeight="semibold" color="red.700">
                          What will be deleted:
                        </Text>
                        <VStack spacing={1} align="start" pl={2}>
                          <Text fontSize="sm" color="red.600">
                            - Your user profile and account information
                          </Text>
                          <Text fontSize="sm" color="red.600">
                            - All simulation jobs and results
                          </Text>
                          <Text fontSize="sm" color="red.600">
                            - Your subscription and billing history
                          </Text>
                          <Text fontSize="sm" color="red.600">
                            - All stored files and data
                          </Text>
                        </VStack>
                      </VStack>
                    </Box>

                    <HStack justify="flex-end">
                      <Button
                        colorScheme="red"
                        variant="solid"
                        onClick={openDeleteModal}
                        leftIcon={<FaTrash />}
                      >
                        Delete My Account
                      </Button>
                    </HStack>
                  </VStack>
                </CardBody>
              </Card>
            </>
          ) : (
            <Card bg={cardBg} shadow="sm">
              <CardBody textAlign="center" py={12}>
                <Icon as={FaUser} color="gray.400" boxSize={16} mb={4} />
                <Heading size="md" color="gray.500" mb={2}>
                  No User Data Available
                </Heading>
                <Text color="gray.600">
                  Unable to load user information at this time.
                </Text>
              </CardBody>
            </Card>
          )}
        </VStack>
      </Container>

      {/* Contact Us Modal */}
      <Modal isOpen={isContactOpen} onClose={onContactClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <HStack spacing={3}>
              <Icon as={FaComments} color="brand.500" boxSize={6} />
              <Text>Contact Us</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <form onSubmit={handleContactSubmit}>
            <ModalBody>
              {contactError && (
                <Alert status="error" borderRadius="md" mb={4}>
                  <AlertIcon />
                  {contactError}
                </Alert>
              )}
              
              <VStack spacing={4}>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} w="full">
                  <FormControl>
                    <FormLabel fontSize="sm" fontWeight="semibold">Name</FormLabel>
                    <Input
                      name="user_name"
                      value={contactForm.user_name}
                      placeholder="Your full name"
                      size="sm"
                      isReadOnly
                      bg="gray.50"
                      _focus={{ bg: "gray.50" }}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel fontSize="sm" fontWeight="semibold">Email</FormLabel>
                    <Input
                      name="user_email"
                      type="email"
                      value={contactForm.user_email}
                      placeholder="your.email@example.com"
                      size="sm"
                      isReadOnly
                      bg="gray.50"
                      _focus={{ bg: "gray.50" }}
                    />
                  </FormControl>
                </SimpleGrid>

                <FormControl isRequired>
                  <FormLabel fontSize="sm" fontWeight="semibold">Subject</FormLabel>
                  <Input
                    name="subject"
                    value={contactForm.subject}
                    onChange={handleContactInputChange}
                    placeholder="Brief description of your inquiry"
                    size="sm"
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel fontSize="sm" fontWeight="semibold">Message</FormLabel>
                  <Textarea
                    name="message"
                    value={contactForm.message}
                    onChange={handleContactInputChange}
                    placeholder="Please provide details about your inquiry..."
                    rows={6}
                    size="sm"
                    resize="vertical"
                  />
                </FormControl>
              </VStack>
            </ModalBody>

            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onContactClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                colorScheme="brand"
                leftIcon={<FaPaperPlane />}
                isLoading={isSubmittingContact}
                loadingText="Sending..."
              >
                Send Message
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>

      {/* Cancel Subscription Confirmation Modal */}
      <Modal isOpen={isCancelOpen} onClose={onCancelClose} size="md" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <HStack spacing={3}>
              <Icon as={FaExclamationTriangle} color="red.500" boxSize={6} />
              <Text>Cancel Subscription</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="start">
              <Text color="gray.700">
                Are you sure you want to cancel your Premium subscription?
              </Text>
              
              <Box bg="red.50" p={4} borderRadius="md" border="1px solid" borderColor="red.200" w="full">
                <VStack spacing={2} align="start">
                  <HStack spacing={2}>
                    <Icon as={FaInfoCircle} color="red.500" boxSize={4} />
                    <Text fontSize="sm" fontWeight="semibold" color="red.700">
                      What happens when you cancel:
                    </Text>
                  </HStack>
                  <VStack spacing={1} align="start" pl={2}>
                    <Text fontSize="sm" color="red.600">
                      - You'll lose access to premium features
                    </Text>
                    <Text fontSize="sm" color="red.600">
                      - Your account will be downgraded to the €1 Starter plan
                    </Text>
                    <Text fontSize="sm" color="red.600">
                      - No additional charges will be made
                    </Text>
                  </VStack>
                </VStack>
              </Box>
              
              {subscriptionDetails && (
                <Box bg="blue.50" p={3} borderRadius="md" w="full">
                  <Text fontSize="sm" color="blue.700">
                    <strong>Current period ends:</strong> {formatSubscriptionDate(subscriptionDetails.current_period_end)}
                  </Text>
                </Box>
              )}
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onCancelClose}>
              Keep Subscription
            </Button>
            <Button
              colorScheme="red"
              onClick={handleCancelSubscription}
              isLoading={isSubmittingContact}
              loadingText="Cancelling..."
            >
              Yes, Cancel Subscription
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Account Confirmation Modal */}
      <Modal 
        isOpen={isDeleteOpen} 
        onClose={onDeleteClose} 
        size="lg" 
        isCentered
        closeOnOverlayClick={!isDeletingAccount}
        closeOnEsc={!isDeletingAccount}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <HStack spacing={3}>
              <Icon as={FaExclamationTriangle} color="red.500" boxSize={6} />
              <Text>Delete Account</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton isDisabled={isDeletingAccount} />
          <form onSubmit={handleDeleteAccount}>
            <ModalBody>
              {deleteError && (
                <Alert status="error" borderRadius="md" mb={4}>
                  <AlertIcon />
                  {deleteError}
                </Alert>
              )}
              
              <VStack spacing={4} align="start">
                <Alert status="error" borderRadius="md">
                  <AlertIcon />
                  <Box>
                    <Text fontWeight="semibold">
                      This will permanently delete your account!
                    </Text>
                    <Text fontSize="sm">
                      This action cannot be undone. All your data will be lost forever.
                    </Text>
                  </Box>
                </Alert>

                <FormControl isRequired>
                  <FormLabel fontSize="sm" fontWeight="semibold">
                    Confirm your email address
                  </FormLabel>
                  <Input
                    name="email"
                    type="email"
                    value={deleteForm.email}
                    onChange={handleDeleteInputChange}
                    placeholder="Enter your email to confirm"
                    size="sm"
                    isReadOnly
                    bg="gray.50"
                    _focus={{ bg: "gray.50" }}
                  />
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    This must match your account email
                  </Text>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel fontSize="sm" fontWeight="semibold">
                    Reason for deletion
                  </FormLabel>
                  <Select
                    name="reason"
                    value={deleteForm.reason}
                    onChange={handleDeleteInputChange}
                    placeholder="Please select a reason"
                    size="sm"
                  >
                    <option value="Not using the service">Not using the service</option>
                    <option value="Too expensive">Too expensive</option>
                    <option value="Found better alternative">Found better alternative</option>
                    <option value="Privacy concerns">Privacy concerns</option>
                    <option value="Technical issues">Technical issues</option>
                    <option value="Poor customer service">Poor customer service</option>
                    <option value="Other">Other</option>
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="semibold">
                    Additional feedback (optional)
                  </FormLabel>
                  <Textarea
                    name="additional_feedback"
                    value={deleteForm.additional_feedback}
                    onChange={handleDeleteInputChange}
                    placeholder="Help us improve by sharing more details..."
                    rows={4}
                    size="sm"
                    resize="vertical"
                  />
                </FormControl>
              </VStack>
            </ModalBody>

            <ModalFooter>
              <Button 
                variant="ghost" 
                mr={3} 
                onClick={onDeleteClose}
                isDisabled={isDeletingAccount}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                colorScheme="red"
                isLoading={isDeletingAccount}
                loadingText="Deleting..."
              >
                Yes, Delete My Account
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>
      <Footer />
    </Box>
    
  );
};

export default Settings;
